import {
  basemapPreset,
  effectiveBasemapCustomization,
  CURATED_BASEMAP_PRESET_IDS,
  type BasemapConfiguration,
  type CuratedBasemapPresetId,
} from './catalog';
import { basemapContextPresentationStatus, basemapPresentationRecipe } from './presentation';
import { isDefaultLabelAppearance } from './labelAppearance';

export const BASEMAP_LABEL_PRESENTATIONS = [
  { id: 'follow-basemap', label: 'Follow basemap' },
  { id: 'standard', label: 'Standard' },
  { id: 'detailed', label: 'Detailed' },
  { id: 'minimal', label: 'Minimal' },
  { id: 'thematic', label: 'Thematic' },
] as const;
export type BasemapLabelPresentationId = (typeof BASEMAP_LABEL_PRESENTATIONS)[number]['id'];
export type ResolvedBasemapLabelPresentation =
  Exclude<BasemapLabelPresentationId, 'follow-basemap'> | 'muted';
export const BASEMAP_LABEL_GROUPS = [
  'citiesTowns',
  'suburbsLocalities',
  'roadNames',
  'routeShields',
  'transitLabels',
  'naturalWaterLabels',
  'otherLabels',
  'pointsOfInterest',
] as const;
export interface BasemapLabelRecipe {
  readonly profile: ResolvedBasemapLabelPresentation;
  readonly opacity: number;
  readonly visibility: Pick<
    ReturnType<typeof basemapPreset>['customizationDefaults']['visibility'],
    (typeof BASEMAP_LABEL_GROUPS)[number]
  >;
}

export function isBasemapLabelPresentationId(value: unknown): value is BasemapLabelPresentationId {
  return BASEMAP_LABEL_PRESENTATIONS.some(({ id }) => id === value);
}

export function resolvedBasemapLabelPresentation(
  configuration: BasemapConfiguration,
  family: CuratedBasemapPresetId,
): ResolvedBasemapLabelPresentation {
  const selected = configuration.labelPresentation ?? 'follow-basemap';
  if (selected !== 'follow-basemap') return selected;
  const context = basemapContextPresentationStatus(configuration, family);
  return context === 'custom' || context === null ? 'standard' : context;
}

export function basemapLabelRecipe(
  family: CuratedBasemapPresetId,
  profile: ResolvedBasemapLabelPresentation,
): BasemapLabelRecipe {
  const recipe = basemapPresentationRecipe(family, profile === 'detailed' ? 'standard' : profile);
  return {
    profile,
    opacity: recipe.labelsOpacity,
    visibility: Object.fromEntries(
      BASEMAP_LABEL_GROUPS.map((group) => [
        group,
        group === 'otherLabels' ? true : recipe.visibility[group],
      ]),
    ) as BasemapLabelRecipe['visibility'],
  };
}

/** Selecting an intent resets label adjustments across families, leaving context intact. */
export function applyBasemapLabelPresentation(
  configuration: BasemapConfiguration,
  intent: BasemapLabelPresentationId,
): BasemapConfiguration {
  if (configuration.presetId === 'none') return configuration;
  const customizationByPreset = Object.fromEntries(
    CURATED_BASEMAP_PRESET_IDS.map((family) => {
      const old = configuration.customizationByPreset[family];
      const profile =
        intent === 'follow-basemap'
          ? resolvedBasemapLabelPresentation(
              { ...configuration, labelPresentation: intent },
              family,
            )
          : intent;
      const recipe = basemapLabelRecipe(family, profile);
      const defaults = basemapPreset(family).customizationDefaults;
      const linked = intent === 'follow-basemap';
      const next = {
        ...old,
        labelsOpacity: linked && recipe.opacity !== defaults.labelsOpacity ? recipe.opacity : null,
        visibility: {
          ...old.visibility,
          ...Object.fromEntries(
            BASEMAP_LABEL_GROUPS.map((group) => [
              group,
              linked && recipe.visibility[group] !== defaults.visibility[group]
                ? recipe.visibility[group]
                : null,
            ]),
          ),
        },
      };
      delete next.labelAppearance;
      return [family, next];
    }),
  ) as BasemapConfiguration['customizationByPreset'];
  return { ...configuration, labelPresentation: intent, customizationByPreset };
}

export function basemapLabelPresentationStatus(
  configuration: BasemapConfiguration,
): BasemapLabelPresentationId | 'custom' | null {
  const family = configuration.presetId;
  if (family === 'none') return null;
  const values = effectiveBasemapCustomization(configuration, family);
  const recipe = basemapLabelRecipe(
    family,
    resolvedBasemapLabelPresentation(configuration, family),
  );
  return values.labelsOpacity === recipe.opacity &&
    isDefaultLabelAppearance(configuration.customizationByPreset[family].labelAppearance) &&
    BASEMAP_LABEL_GROUPS.every((group) => values.visibility[group] === recipe.visibility[group])
    ? (configuration.labelPresentation ?? 'follow-basemap')
    : 'custom';
}
